"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { megaMenus, navigation, site, type MegaMenu } from "@/lib/site";
import { ConsultationModal } from "@/components/forms/consultation-modal";
import { cn } from "@/lib/utils";
import { useTheme } from "@/components/theme-provider";
import { ButtonLink } from "@/components/ui/button";
import { MoonIcon, SunIcon, ChevronIcon } from "@/components/icons";
import { BrandLink } from "@/components/logo";
import { googlePartnerBadge, googlePartnerProfile } from "@/lib/google-partner";

type MegaLinkItem = MegaMenu["groups"][number]["links"][number];

function MegaLink({ link, onNavigate }: { link: MegaLinkItem; onNavigate: () => void }) {
  return (
    <Link href={link.href} className="mega-link" onClick={onNavigate}>
      <span className="mega-mark" aria-hidden="true">
        {link.mark}
      </span>
      <span className="mega-copy">
        <span className="mega-link-title">
          <b>{link.label}</b>
          {link.badge ? <span className="soon-tag mega-badge">{link.badge}</span> : null}
        </span>
        <small>{link.detail}</small>
      </span>
    </Link>
  );
}

function PartnerMenuBadge({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={cn("mega-partner", mobile && "mega-partner-mobile")}>
      <a
        href={googlePartnerProfile}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Verify Data Bid Machine's Google Partner status (opens in a new tab)"
      >
        {/* Google requires the online badge to link to the public Partner profile. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={googlePartnerBadge} alt="Google Partner badge" width="88" height="85" loading="lazy" decoding="async" />
      </a>
      <p>We’re a Google Partner <span>for Google Ads.</span></p>
    </div>
  );
}

type MegaKey = "services" | "platform" | "verticals";

function MegaTrigger({
  label,
  open,
  onClick,
  onFocus,
}: {
  label: string;
  open: boolean;
  onClick: () => void;
  onFocus?: () => void;
}) {
  return (
    <button
      type="button"
      className="nav-link"
      aria-expanded={open}
      aria-haspopup="true"
      onClick={onClick}
      onFocus={onFocus}
    >
      {label}
      <span className="caret" aria-hidden="true">
        <ChevronIcon />
      </span>
    </button>
  );
}

function canHoverMegaMenu() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches &&
    window.innerWidth > 1200
  );
}

function MegaPanel({
  menu,
  id,
  onNavigate,
  onMouseEnter,
  onMouseLeave,
}: {
  menu: MegaMenu;
  id: string;
  onNavigate: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  const singleGroup = menu.groups.length === 1;
  const lengths = menu.groups.map((group) => group.links.length);
  const maxLinks = Math.max(0, ...lengths);
  const minLinks = Math.min(maxLinks, ...lengths);
  const syncRows = !singleGroup && maxLinks - minLinks <= 1;
  const useSections = id === "verticals" && menu.groups.length > 1;

  return (
    <div
      className={cn("mega-panel", id === "verticals" && "mega-panel-verticals")}
      id={`mega-${id}`}
      role="region"
      aria-label={menu.eyebrow}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={cn("mega-intro", id === "platform" && "mega-intro-blue", id === "services" && "mega-intro-services")}>
        <p className="eyebrow">{menu.eyebrow}</p>
        <h3>{menu.title}</h3>
        {menu.subtitle ? <p className="mega-intro-sub">{menu.subtitle}</p> : null}
        <p className="mega-intro-body">{menu.description}</p>
        {id === "services" ? <PartnerMenuBadge /> : null}
        <Link href={menu.cta.href} className="mega-intro-cta" onClick={onNavigate}>
          {menu.cta.label} <span aria-hidden="true">→</span>
        </Link>
      </div>
      {useSections ? (
        <div className="mega-sections">
          {menu.groups.map((group) => (
            <div key={group.label ?? "group"} className="mega-section">
              {group.label ? <p className="mega-section-label">{group.label}</p> : null}
              {group.description ? <p className="mega-section-desc">{group.description}</p> : null}
              <div className="mega-section-links">
                {group.links.map((link) => (
                  <MegaLink key={link.href} link={link} onNavigate={onNavigate} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : singleGroup ? (
        <div
          className={cn(
            "mega-links",
            menu.groups[0].links.length <= 4 && "mega-links-compact",
          )}
        >
          {menu.groups[0].links.map((link) => (
            <MegaLink key={link.href} link={link} onNavigate={onNavigate} />
          ))}
        </div>
      ) : (
        <div
          className={cn("mega-groups", syncRows && "mega-groups-sync")}
          style={syncRows ? ({ "--mega-rows": maxLinks } as CSSProperties) : undefined}
        >
          {menu.groups.map((group) => (
            <div key={group.label ?? "group"} className="mega-group">
              {group.label && <p className="mega-group-label">{group.label}</p>}
              {group.description && <p className="mega-group-desc">{group.description}</p>}
              {group.links.map((link) => (
                <MegaLink key={link.href} link={link} onNavigate={onNavigate} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ThemeToggleButton() {
  const { theme, toggle } = useTheme();
  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={toggle}
      suppressHydrationWarning
      data-theme={theme}
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
    >
      <span className="theme-icon theme-icon-sun" aria-hidden="true">
        <SunIcon />
      </span>
      <span className="theme-icon theme-icon-moon" aria-hidden="true">
        <MoonIcon />
      </span>
    </button>
  );
}

export function Header() {
  const [consultationOpen, setConsultationOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mega, setMega] = useState<MegaKey | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileMega, setMobileMega] = useState<MegaKey | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const megaCloseTimer = useRef<number | null>(null);
  const pathname = usePathname();
  const practiceVertical = pathname === "/verticals/healthcare" || pathname === "/verticals/dental-practices";

  const clearMegaCloseTimer = () => {
    if (megaCloseTimer.current !== null) {
      window.clearTimeout(megaCloseTimer.current);
      megaCloseTimer.current = null;
    }
  };

  const openMega = (key: MegaKey) => {
    clearMegaCloseTimer();
    setMega(key);
  };

  const scheduleMegaClose = () => {
    if (!canHoverMegaMenu()) return;
    clearMegaCloseTimer();
    megaCloseTimer.current = window.setTimeout(() => setMega(null), 220);
  };

  useEffect(() => {
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setScrolled(window.scrollY > 24));
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => {
      window.removeEventListener("scroll", update);
      cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("menu-lock", mobileOpen);
    return () => document.body.classList.remove("menu-lock");
  }, [mobileOpen]);

  const [prevPathname, setPrevPathname] = useState(pathname);
  if (prevPathname !== pathname) {
    setPrevPathname(pathname);
    setMega(null);
    setMobileOpen(false);
    setMobileMega(null);
  }

  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileOpen(false);
        setMobileMega(null);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  useEffect(() => {
    if (!mega) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMega(null);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) setMega(null);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [mega]);

  useEffect(() => () => clearMegaCloseTimer(), []);

  const closeAll = () => {
    setMega(null);
    setMobileOpen(false);
    setMobileMega(null);
  };

  return (
    <>
      <header ref={headerRef} className={cn("site-header", scrolled && "scrolled", mobileOpen && "menu-open", mega && "mega-open")}>
        <div className="header-inner">
        <BrandLink />

        <nav className="primary-nav" aria-label="Primary navigation">
          {navigation.primary.map((item) => {
            if (item.type === "mega") {
              return (
                <div
                  key={item.label}
                  className={cn("nav-mega", mega === item.key && "is-open")}
                  data-mega
                  onMouseEnter={() => {
                    if (canHoverMegaMenu()) openMega(item.key as MegaKey);
                  }}
                  onMouseLeave={scheduleMegaClose}
                >
                  <MegaTrigger
                    label={item.label}
                    open={mega === item.key}
                    onClick={() => setMega(mega === item.key ? null : (item.key as MegaKey))}
                    onFocus={() => openMega(item.key as MegaKey)}
                  />
                  <MegaPanel
                    menu={megaMenus[item.key as MegaKey]}
                    id={item.key}
                    onNavigate={() => setMega(null)}
                    onMouseEnter={clearMegaCloseTimer}
                    onMouseLeave={scheduleMegaClose}
                  />
                </div>
              );
            }
            return (
              <Link
                key={item.label}
                href={item.href ?? "/"}
                prefetch={item.href === "/blogs" ? false : undefined}
                className="nav-link"
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="header-actions">
          <ThemeToggleButton />
          <button type="button" className="btn btn-ghost btn-sm consultation-header-cta" aria-haspopup="dialog" onClick={() => { closeAll(); setConsultationOpen(true); }}>
            Book a Free Consultation
          </button>
          <ButtonLink href="/growth-gap-audit" variant="accent" size="sm" withArrow className="nav-cta">
            {practiceVertical ? "Request a Review" : "Talk to an Expert"}
          </ButtonLink>
          <button
            type="button"
            className="menu-toggle"
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileOpen((open) => !open)}
          >
            <span className="sr-only">Toggle navigation</span>
            <span className="burger" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
        </div>
        </div>
      </header>
      <div
        id="mobile-menu"
        className={cn("mobile-menu", mobileOpen && "is-open", scrolled && "is-scrolled")}
        aria-hidden={!mobileOpen}
      >
        <nav aria-label="Mobile navigation">
          {navigation.primary.map((item) => {
            if (item.type === "mega") {
              const menu = megaMenus[item.key as MegaKey];
              const open = mobileMega === item.key;
              return (
                <div key={item.label}>
                  <button
                    type="button"
                    className="m-link"
                    aria-expanded={open}
                    onClick={() => setMobileMega(open ? null : (item.key as MegaKey))}
                  >
                    <span>{item.label}</span>
                    <span className={cn("m-caret", open && "is-open")} aria-hidden="true">
                      <ChevronIcon />
                    </span>
                  </button>
                  {open && (
                    <div className="m-mega-panel">
                      {menu.groups.map((group) => (
                        <div key={group.label ?? "group"} className="m-mega-group">
                          {group.label && <p className="m-mega-group-label">{group.label}</p>}
                          {group.description && <p className="m-mega-group-desc">{group.description}</p>}
                          <div className="m-mega-list">
                            {group.links.map((link) => (
                              <div key={link.href} className="m-mega-entry">
                                <Link
                                  href={link.href}
                                  className="m-mega-link"
                                  onClick={closeAll}
                                >
                                  <span className="m-mega-link-label">
                                    {link.label}
                                    {link.badge && <span className="soon-tag m-mega-badge">{link.badge}</span>}
                                  </span>
                                  <small>{link.detail}</small>
                                </Link>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {item.key === "services" ? <PartnerMenuBadge mobile /> : null}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={item.label}
                href={item.href ?? "/"}
                prefetch={item.href === "/blogs" ? false : undefined}
                className="m-link"
                onClick={closeAll}
              >
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="mobile-menu-cta">
          <ButtonLink href="/growth-gap-audit" variant="accent" withArrow className="btn-full" onClick={closeAll}>
            {practiceVertical ? "Request a Review" : "Talk to an Expert"}
          </ButtonLink>
          <button type="button" className="btn btn-ghost btn-full" aria-haspopup="dialog" onClick={() => { closeAll(); setConsultationOpen(true); }}>
            Book a Free Consultation
          </button>
        </div>
        <div className="mobile-menu-meta">
          <span className="text-[12px] font-semibold text-dbm-faint">{site.region}</span>
          <ThemeToggleButton />
        </div>
      </div>
      <ConsultationModal open={consultationOpen} onClose={() => setConsultationOpen(false)} />
    </>
  );
}
